import React from "react";
import { motion, useReducedMotion } from "framer-motion";

// Decorative background for the auth brand panel: a circular tunnel bore drawn
// as a blueprint, its rings travelling outward toward the viewer — reading at
// once as a TBM section receding to a vanishing point and as a slow ripple.
//
// It replaced a faux dashboard. Customers read that mock's KPI tiles and task
// table as their own live data and asked why analytics were on the login page,
// so this carries NO figures at all — only draughting annotation. Keep it that
// way: the moment a number appears here it can be mistaken for a reading.
//
// aria-hidden, zero focusable elements. Frozen entirely under reduced motion.

const VB = 1000; // square viewBox; the element is sized 116% and slice-cropped
const VX = 620; // vanishing point x — right of and below centre so the sparse
const VY = 520; // vanishing point y   deep end clears the slide title
const R = 400; // bore radius at scale 1
const RATIO = 0.78; // ring-to-ring scale step
const TRAVEL = 7; // seconds for one ring-step

// Depth is expressed as fixed alpha per ring — a fade mask would need a
// gradient, and the design system bans those (docs/pms-design-system.md §8).
// Index 0 is the nearest ring and sits at 0 so rings dissolve before they can
// sweep across the panel.
//
// THE PEAK IS A CONTRAST BUDGET, NOT A TASTE SETTING. Rings pass under the
// slide copy. White at 0.24 over #2557d6 lifts the local background to #597FE0
// (L 0.228), where 0.82 white body copy is 3.08:1 — an AA failure. At the 0.12
// cap below the background is #3F6BDB (L 0.167) and 0.96 white copy is 4.59:1.
// Raising this array means raising .pxauth__brandSub / __eyebrow with it.
const OPACITY = [
  0, 0.03, 0.055, 0.08, 0.104, 0.12, 0.118, 0.108, 0.096, 0.084, 0.072, 0.06,
  0.05, 0.04, 0.032, 0.024, 0.016, 0.01,
];

const SCALES = OPACITY.map((_, k) => RATIO ** k);

// Diagonal rays from the vanishing point through the bore. These are the
// strongest depth cue and are scale-invariant, so they must stay OUTSIDE the
// animated group — travelling with it would make them shear.
const RAY_S = 1.85;
const D = R * RAY_S * Math.SQRT1_2; // 45° on the bore circle
const RAYS = [
  [VX - D, VY - D],
  [VX + D, VY - D],
  [VX - D, VY + D],
  [VX + D, VY + D],
];

const AXIS = R * RAY_S;
const LINE = "rgba(255, 255, 255, 0.1)"; // rays/axes cross the copy too

const TunnelBlueprint: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const still = !!reduceMotion;

  return (
    <svg
      className="pxauth__bg"
      viewBox={`0 0 ${VB} ${VB}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <pattern
          id="pxauthBlueprintGrid"
          width="40"
          height="40"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M40 0H0V40"
            fill="none"
            stroke="rgba(255, 255, 255, 0.035)"
            strokeWidth="1"
          />
        </pattern>
      </defs>

      {/* Graph paper */}
      <rect width={VB} height={VB} fill="url(#pxauthBlueprintGrid)" />

      {/* Convergence rays + setting-out axes, static */}
      <g
        fill="none"
        stroke={LINE}
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      >
        {RAYS.map(([x, y]) => (
          <line key={`${x},${y}`} x1={VX} y1={VY} x2={x} y2={y} />
        ))}
        <g strokeDasharray="18 6 3 6" opacity="0.75">
          <line x1={VX - AXIS} y1={VY} x2={VX + AXIS} y2={VY} />
          <line x1={VX} y1={VY - AXIS} x2={VX} y2={VY + AXIS} />
        </g>
      </g>

      {/* The bore. One transform on one group is the whole travel: because the
          rings are a geometric series, scaling by exactly 1/RATIO lands ring k
          where ring k-1 started, so the loop closes with no seam. Each ring
          hands its alpha to its neighbour over the same cycle for the same
          reason — static alphas would pop on restart. */}
      <motion.g
        style={{ transformBox: "view-box", transformOrigin: `${VX}px ${VY}px` }}
        animate={still ? undefined : { scale: [1, 1 / RATIO] }}
        transition={{ duration: TRAVEL, repeat: Infinity, ease: "linear" }}
      >
        {SCALES.map((s, k) => (
          <motion.circle
            key={s}
            cx={VX}
            cy={VY}
            r={R * s}
            fill="none"
            stroke="#ffffff"
            strokeWidth="1.25"
            /* Hairlines stay hairlines as the group scales — that uniform
               line weight is what reads as a blueprint. */
            vectorEffect="non-scaling-stroke"
            style={{ opacity: OPACITY[k] }}
            animate={
              still ? undefined : { opacity: [OPACITY[k], OPACITY[k - 1] ?? 0] }
            }
            transition={{ duration: TRAVEL, repeat: Infinity, ease: "linear" }}
          />
        ))}
      </motion.g>

      {/* Draughting annotation — deliberately wordmarks only, never values.
          Anchored to the bore, not the viewBox: `slice` scaling crops to a
          centred square, so corner-anchored text is the first thing lost. */}
      <g
        fill="rgba(255, 255, 255, 0.28)"
        fontFamily="Inter, sans-serif"
        fontSize="13"
        fontWeight="500"
        letterSpacing="2.5"
      >
        <text x={VX - 330} y={VY + R * 0.95}>
          SECTION A&#8212;A
        </text>
        <text x={VX + 14} y={VY - R * 0.5} fontSize="11" letterSpacing="1.5">
          CL
        </text>
      </g>
    </svg>
  );
};

export default TunnelBlueprint;
